import React, { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  BookOpen, MapPin, BarChart2, Brain, ChevronRight,
  Lock, CheckCircle, Play, Star, Clock, ArrowLeft,
  Layers, PenTool, Trophy, TrendingUp, AlertTriangle,
  ChevronDown, ChevronUp, Award, Hash
} from 'lucide-react'
import {
  getUnitById, DOMAIN_LABELS, DOMAIN_EMOJIS, GRADE_SHORT,
  type SubTopic,
} from '../data/syllabus'
import { useProgressStore, useUnitProgress, useMisconceptions, PASS_SCORE, TOTAL_LEARN_STEPS } from '../store/progressStore'
import MisconceptionChart from '../components/MisconceptionChart'
import LearnPage from './LearnPage'
import N1LearnPage from './N1LearnPage'
import N1TestPage from './N1TestPage'
import N1RealWorldPage from './N1RealWorldPage'
import N1TutorPage from './N1TutorPage'
import N2LearnPage from './N2LearnPage'
import N2TestPage from './N2TestPage'
import N2RealWorldPage from './N2RealWorldPage'
import N2TutorPage from './N2TutorPage'
import N3LearnPage from './N3LearnPage'
import N3TestPage from './N3TestPage'
import N3TutorPage from './N3TutorPage'
import N4LearnPage from './N4LearnPage'
import N4TestPage from './N4TestPage'
import N4TutorPage from './N4TutorPage'
import N4RealWorldPage from './N4RealWorldPage'
import N1_2RealWorldPage from './N1_2RealWorldPage'
import A1LearnPage from './A1LearnPage'
import A1TestPage from './A1TestPage'
import A1TutorPage from './A1TutorPage'
import A1_2TutorPage from './A1_2TutorPage'
import A1_2TestPage from './A1_2TestPage'
import A1RealWorldPage from './A1RealWorldPage'
import A1_2RealWorldPage from './A1_2RealWorldPage'
import G1_1RealWorldPage from './G1_1RealWorldPage'
import G1_1LearnPage from './G1_1LearnPage'
import G1_1TutorPage from './G1_1TutorPage'
import G1_1TestPage from './G1_1TestPage'
import G1_2LearnPage from './G1_2LearnPage'
import G1_2RealWorldPage from './G1_2RealWorldPage'
import G1_2TutorPage from './G1_2TutorPage'
import G1_2TestPage from './G1_2TestPage'
import G1_3LearnPage from './G1_3LearnPage'
import G1_3RealWorldPage from './G1_3RealWorldPage'
import G1_3TutorPage from './G1_3TutorPage'
import G1_3TestPage from './G1_3TestPage'
import F1_1LearnPage from './F1_1LearnPage'
import F1_1RealWorldPage from './F1_1RealWorldPage'
import F1_1TutorPage from './F1_1TutorPage'
import F1_1TestPage from './F1_1TestPage'
import S1_1LearnPage from './S1_1LearnPage'
import S1_1RealWorldPage from './S1_1RealWorldPage'
import S1_1TutorPage from './S1_1TutorPage'
import S1_1TestPage from './S1_1TestPage'
import A2_1LearnPage from './A2_1LearnPage'
import A2_2LearnPage from './A2_2LearnPage'
import A2_3LearnPage from './A2_3LearnPage'
import A2_1RealWorldPage from './A2_1RealWorldPage'
import A2_2RealWorldPage from './A2_2RealWorldPage'
import A2_3RealWorldPage from './A2_3RealWorldPage'
import A2_1ProblemPage from './A2_1ProblemPage'
import A2_2ProblemPage from './A2_2ProblemPage'
import A2_3ProblemPage from './A2_3ProblemPage'
import A2_1TestPage from './A2_1TestPage'
import A2_2TestPage from './A2_2TestPage'
import A2_3TestPage from './A2_3TestPage'
import F2_2LearnPage from './F2_2LearnPage'
import F2_2RealWorldPage from './F2_2RealWorldPage'
import F2_2ProblemPage from './F2_2ProblemPage'
import F2_2TestPage from './F2_2TestPage'
import RealWorldPage from './RealWorldPage'
import GraphPage from './GraphPage'
import TutorPage from './TutorPage'
import ProblemPage from './ProblemPage'
import TestPage from './TestPage'

// ── 탭 타입 ──────────────────────────────────────────────────────────────────
type UnitTab = 'learn' | 'realworld' | 'graph' | 'tutor' | 'problems' | 'test'

// ── N1-1 소인수분해 탐색기 (인라인 인터랙티브 도구) ─────────────────────────────
function N1ExplorerPanel() {
  const [input, setInput] = useState('')
  const [result, setResult] = useState<{ factors: [number, number][]; expression: string } | null>(null)
  const [error, setError] = useState('')
  const [history, setHistory] = useState<Array<{ n: number; expr: string }>>([]
  )

  const factorize = (n: number): [number, number][] => {
    const factors: [number, number][] = []
    let d = 2
    let num = n
    while (d * d <= num) {
      let cnt = 0
      while (num % d === 0) { cnt++; num = Math.floor(num / d) }
      if (cnt > 0) factors.push([d, cnt])
      d++
    }
    if (num > 1) factors.push([num, 1])
    return factors
  }

  const toExpression = (factors: [number, number][]) =>
    factors.map(([p, e]) => e === 1 ? `${p}` : `${p}^${e}`).join(' × ')

  const handleFactorize = () => {
    const n = parseInt(input, 10)
    if (isNaN(n) || n < 2) { setError('2 이상의 자연수를 입력하세요.'); setResult(null); return }
    if (n > 9999) { setError('9999 이하의 수를 입력하세요.'); setResult(null); return }
    setError('')
    const factors = factorize(n)
    const expr = `${n} = ${toExpression(factors)}`
    setResult({ factors, expression: expr })
    setHistory(h => [{ n, expr }, ...h].slice(0, 8))
  }

  const EXAMPLES = [12, 36, 60, 72, 100, 360]

  // GCD & LCM 계산기
  const [gInput, setGInput] = useState('')
  const [gResult, setGResult] = useState<{ gcd: number; lcm: number; aExpr: string; bExpr: string } | null>(null)
  const [gError, setGError] = useState('')

  const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b)
  const lcm = (a: number, b: number): number => Math.floor(a / gcd(a, b)) * b

  const handleGCD = () => {
    const parts = gInput.split(/[,\s]+/).map(s => parseInt(s.trim(), 10)).filter(n => !isNaN(n))
    if (parts.length !== 2) { setGError('두 수를 쉼표로 구분해 입력하세요. 예: 12, 18'); setGResult(null); return }
    const [a, b] = parts
    if (a < 2 || b < 2 || a > 9999 || b > 9999) { setGError('2~9999 사이의 두 수를 입력하세요.'); setGResult(null); return }
    setGError('')
    const g = gcd(a, b), l = lcm(a, b)
    const aFactors = factorize(a), bFactors = factorize(b)
    setGResult({ gcd: g, lcm: l, aExpr: `${a} = ${toExpression(aFactors)}`, bExpr: `${b} = ${toExpression(bFactors)}` })
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
          <Hash size={20} className="text-white" />
        </div>
        <div>
          <h2 className="text-xl font-black text-slate-800">소인수분해 탐색기</h2>
          <p className="text-sm text-slate-500">숫자를 입력하면 소인수분해 · GCD · LCM을 자동으로 계산합니다</p>
        </div>
      </div>

      {/* ── 소인수분해 계산기 ── */}
      <div className="bg-white rounded-2xl border-2 border-blue-100 p-5 shadow-sm">
        <h3 className="font-black text-slate-800 mb-3 flex items-center gap-2">
          <span className="text-lg">🌳</span> 소인수분해 계산기
        </h3>
        <div className="flex gap-2 mb-3">
          <input
            type="number" min={2} max={9999}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleFactorize()}
            placeholder="숫자 입력 (2~9999)"
            className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:border-blue-400 focus:outline-none"
          />
          <button
            onClick={handleFactorize}
            className="px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-black transition-colors"
          >
            분해
          </button>
        </div>
        {/* 예시 버튼 */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {EXAMPLES.map(n => (
            <button key={n} onClick={() => { setInput(String(n)); setTimeout(() => {
              const f = factorize(n)
              const expr = `${n} = ${toExpression(f)}`
              setResult({ factors: f, expression: expr })
              setHistory(h => [{ n, expr }, ...h].slice(0, 8))
              setError('')
            }, 0) }}
              className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 text-slate-600 hover:text-blue-700 font-mono transition-colors"
            >
              {n}
            </button>
          ))}
        </div>
        {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
        {result && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
            <p className="text-lg font-black text-blue-800 font-mono mb-3">{result.expression}</p>
            {/* 나눗셈 사다리 시각화 */}
            <div className="bg-white rounded-xl p-3 border border-blue-100">
              <p className="text-xs font-bold text-slate-500 mb-2">📐 나눗셈 사다리</p>
              {(() => {
                const rows: { d: number; q: number }[] = []
                let n = parseInt(input, 10)
                for (const [p, e] of result.factors) {
                  for (let i = 0; i < e; i++) { rows.push({ d: p, q: Math.floor(n / p) }); n = Math.floor(n / p) }
                }
                return (
                  <div className="font-mono text-sm space-y-0.5">
                    {rows.map((r, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <span className="w-6 text-right text-blue-600 font-bold">{r.d}</span>
                        <span className="text-slate-300">│</span>
                        <span className="text-slate-700">{i === 0 ? parseInt(input, 10) : rows[i-1].q}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-1">
                      <span className="w-6" />
                      <span className="text-slate-300">└</span>
                      <span className="font-black text-emerald-600">1</span>
                    </div>
                  </div>
                )
              })()}
            </div>
            {/* 소인수 태그 */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {result.factors.map(([p, e]) => (
                <span key={p} className="bg-blue-500 text-white text-xs font-black px-2.5 py-1 rounded-full font-mono">
                  {p}{e > 1 ? <sup>{e}</sup> : ''}
                </span>
              ))}
            </div>
          </div>
        )}
        {/* 히스토리 */}
        {history.length > 1 && (
          <div className="mt-4">
            <p className="text-xs font-bold text-slate-400 mb-2">최근 계산</p>
            <div className="flex flex-wrap gap-2">
              {history.slice(1).map((h, i) => (
                <button key={i}
                  onClick={() => { setInput(String(h.n)); setResult({ factors: factorize(h.n), expression: h.expr }); setError('') }}
                  className="text-xs px-3 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-mono transition-colors"
                >{h.expr}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── GCD · LCM 계산기 ── */}
      <div className="bg-white rounded-2xl border-2 border-teal-100 p-5 shadow-sm">
        <h3 className="font-black text-slate-800 mb-3 flex items-center gap-2">
          <span className="text-lg">🤝</span> GCD · LCM 계산기
        </h3>
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={gInput}
            onChange={e => setGInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleGCD()}
            placeholder="두 수 입력 예: 12, 18"
            className="flex-1 border-2 border-slate-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:border-teal-400 focus:outline-none"
          />
          <button
            onClick={handleGCD}
            className="px-5 py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-xl text-sm font-black transition-colors"
          >
            계산
          </button>
        </div>
        {/* 빠른 예시 */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {[['12,18'], ['24,36'], ['48,72'], ['15,20'], ['60,90']].map(([ex]) => (
            <button key={ex} onClick={() => {
              setGInput(ex)
              const [a, b] = ex.split(',').map(Number)
              const g = gcd(a,b), l = lcm(a,b)
              setGResult({ gcd: g, lcm: l, aExpr: `${a} = ${toExpression(factorize(a))}`, bExpr: `${b} = ${toExpression(factorize(b))}` })
              setGError('')
            }}
              className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 hover:bg-teal-50 hover:border-teal-200 text-slate-600 hover:text-teal-700 font-mono transition-colors"
            >
              {ex}
            </button>
          ))}
        </div>
        {gError && <p className="text-xs text-red-500 mb-2">{gError}</p>}
        {gResult && (
          <div className="space-y-3">
            {/* 소인수분해 표시 */}
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200 font-mono text-sm space-y-1">
              <p className="text-slate-700">{gResult.aExpr}</p>
              <p className="text-slate-700">{gResult.bExpr}</p>
            </div>
            {/* GCD / LCM 결과 */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border-2 border-teal-200 rounded-xl p-4 text-center">
                <p className="text-xs font-bold text-teal-600 mb-1">🤝 최대공약수 GCD</p>
                <p className="text-3xl font-black text-teal-700">{gResult.gcd}</p>
                <p className="text-xs text-teal-500 mt-1">공통 소인수 × 낮은 지수</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-4 text-center">
                <p className="text-xs font-bold text-purple-600 mb-1">🔄 최소공배수 LCM</p>
                <p className="text-3xl font-black text-purple-700">{gResult.lcm}</p>
                <p className="text-xs text-purple-500 mt-1">모든 소인수 × 높은 지수</p>
              </div>
            </div>
            {/* 검증 */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
              <p className="text-xs font-bold text-amber-700">
                ✅ 검증: GCD × LCM = {gResult.gcd} × {gResult.lcm} = {gResult.gcd * gResult.lcm}
                {' '}= {gInput.split(',').map(Number).reduce((a,b) => a*b, 1)} (두 수의 곱) ✓
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── 공식 요약 ── */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Star size={14} className="text-amber-500" /> 핵심 공식 요약
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { title: '소인수분해', formula: 'n = p₁ᵃ × p₂ᵇ × ...', tip: '가장 작은 소수부터 나누기', color: 'bg-blue-50 border-blue-200 text-blue-800' },
            { title: 'GCD (최대공약수)', formula: '공통 소인수 × 낮은 지수', tip: '나누기·타일 문제에 사용', color: 'bg-teal-50 border-teal-200 text-teal-800' },
            { title: 'LCM (최소공배수)', formula: '모든 소인수 × 높은 지수', tip: '만나기·주기 문제에 사용', color: 'bg-purple-50 border-purple-200 text-purple-800' },
          ].map(item => (
            <div key={item.title} className={`border rounded-xl p-3 ${item.color}`}>
              <p className="text-xs font-black mb-1">{item.title}</p>
              <p className="text-sm font-mono font-bold mb-1.5">{item.formula}</p>
              <p className="text-xs opacity-70">📌 {item.tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── 진도 요약 패널 (F2-1 전용) ────────────────────────────────────────────────
function ProgressPanel({
  unitId,
  learnCompletedCount,
  learnPct,
  problemsSolved,
  testBestScore,
  testCount,
  testPassed,
}: {
  unitId: string
  learnCompletedCount: number
  learnPct: number
  problemsSolved: number
  testBestScore: number
  testCount: number
  testPassed: boolean
}) {
  const [showChart, setShowChart] = useState(false)

  const items = [
    {
      icon: <BookOpen size={14} className="text-blue-500" />,
      label: '개념 학습',
      value: `${learnCompletedCount} / ${TOTAL_LEARN_STEPS}`,
      pct: learnPct,
      bar: 'bg-blue-400',
      done: learnCompletedCount >= TOTAL_LEARN_STEPS,
    },
    {
      icon: <PenTool size={14} className="text-purple-500" />,
      label: '문제풀이',
      value: `${problemsSolved} / 15`,
      pct: Math.round((problemsSolved / 15) * 100),
      bar: 'bg-purple-400',
      done: problemsSolved >= 15,
    },
    {
      icon: <Trophy size={14} className="text-yellow-500" />,
      label: '실전 TEST',
      value: testCount > 0 ? `${testBestScore}점` : '미응시',
      pct: testBestScore,
      bar: testPassed ? 'bg-emerald-400' : 'bg-orange-400',
      done: testPassed,
    },
  ]

  return (
    <div className="bg-white/80 border border-slate-200 rounded-2xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
          <TrendingUp size={14} className="text-primary-500" />
          학습 진도 현황
        </h3>
        <button
          onClick={() => setShowChart(!showChart)}
          className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 transition-colors"
        >
          <AlertTriangle size={12} />
          오개념 분석
          {showChart ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </div>

      <div className="space-y-2.5">
        {items.map(item => (
          <div key={item.label} className="flex items-center gap-2">
            {item.icon}
            <span className="text-xs text-slate-600 w-20 font-medium">{item.label}</span>
            <div className="flex-1 bg-slate-100 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-700 ${item.bar}`}
                style={{ width: `${Math.min(item.pct, 100)}%` }}
              />
            </div>
            <span className={`text-xs font-bold w-14 text-right ${item.done ? 'text-emerald-600' : 'text-slate-600'}`}>
              {item.done && <CheckCircle size={10} className="inline mr-0.5" />}
              {item.value}
            </span>
          </div>
        ))}
      </div>

      {/* 단원 합격 배지 */}
      {testPassed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 flex items-center gap-2">
          <Award size={14} className="text-emerald-500 flex-shrink-0" />
          <p className="text-xs font-bold text-emerald-700">🎉 단원 합격! 최고 점수 {testBestScore}점 ({testCount}회 응시)</p>
        </div>
      )}

      {/* 오개념 차트 (토글) */}
      {showChart && (
        <div className="mt-3">
          <MisconceptionChart unitId={unitId} />
        </div>
      )}
    </div>
  )
}

const STATUS_CONFIG = {
  completed:   { icon: CheckCircle, color: 'text-emerald-500' },
  in_progress: { icon: Play,        color: 'text-blue-500' },
  available:   { icon: Star,        color: 'text-orange-400' },
  locked:      { icon: Lock,        color: 'text-slate-300' },
}

// 학습 단계 ID → LearnPage에서 자동 선택하는 방법:
// LearnPage에 defaultStepId prop을 추가하여 전달합니다.

// ── 유닛 상세 페이지 ──────────────────────────────────────────────────────────
export default function UnitDetailPage() {
  const { unitId } = useParams<{ unitId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<UnitTab>('learn')
  // 오답→개념 연결: TestPage에서 요청한 학습 단계 ID
  const [learnTargetStepId, setLearnTargetStepId] = useState<string | null>(null)

  const found = unitId ? getUnitById(unitId) : null

  // ── progressStore 실시간 진도 ──────────────────────────────────────────────
  const unitProgress = useUnitProgress(unitId ?? 'F2-1')
  const learnCompletedCount = unitProgress.learnCompletedCount
  const learnPct = Math.round((learnCompletedCount / TOTAL_LEARN_STEPS) * 100)
  const testBestScore = unitProgress.bestScore
  const testPassed    = unitProgress.passed
  const testCount     = unitProgress.testRecords.length
  const problemsSolved = Object.values(unitProgress.problems).filter(p => p.solved).length

  if (!found) {
    return (
      <div className="p-8 text-center">
        <p className="text-slate-500 text-lg">유닛을 찾을 수 없습니다.</p>
        <Link to="/syllabus" className="mt-4 inline-block text-primary-600 hover:underline">
          ← 전체 커리큘럼으로
        </Link>
      </div>
    )
  }

  const { chapter, unit } = found
  const color = chapter.color
  const completedCount = unit.topics.filter(t => t.status === 'completed').length
  const pct = Math.round((completedCount / unit.topics.length) * 100)

  // 이 유닛에 실제 콘텐츠가 있는지
  const hasLearnContent  = unit.contentReady === true
  const hasRealWorld     = unit.id === 'F2-1' || unit.id === 'F2-2' || unit.id === 'N1-1' || unit.id === 'N1-2' || unit.id === 'N2-1' || unit.id === 'N2-2' || unit.id === 'A1-1' || unit.id === 'A1-2' || unit.id === 'G1-1' || unit.id === 'G1-2' || unit.id === 'G1-3' || unit.id === 'F1-1' || unit.id === 'S1-1' || unit.id === 'A2-1' || unit.id === 'A2-2' || unit.id === 'A2-3'
  const hasGraph         = unit.id === 'F2-1'
  const hasExplorer      = unit.id === 'N1-1'  // 소인수분해 탐색기 (N1-1 전용)
  const hasTutor         = true  // AI 튜터는 항상
  const hasProblems      = unit.id === 'F2-1' || unit.id === 'F2-2' || unit.id === 'A2-1' || unit.id === 'A2-2' || unit.id === 'A2-3'  // 문제풀이
  const hasTest          = unit.id === 'F2-1' || unit.id === 'F2-2' || unit.id === 'N1-1' || unit.id === 'N1-2' || unit.id === 'N2-1' || unit.id === 'N2-2' || unit.id === 'A1-1' || unit.id === 'A1-2' || unit.id === 'G1-1' || unit.id === 'G1-2' || unit.id === 'G1-3' || unit.id === 'F1-1' || unit.id === 'S1-1' || unit.id === 'A2-1' || unit.id === 'A2-2' || unit.id === 'A2-3'  // 실전 TEST

  // N1-1/N1-2: '그래프 탐색' → '소인수 탐색기', '문제풀이' 탭 숨김
  const tabs: { id: UnitTab; label: string; icon: React.ReactNode; available: boolean; badge?: string; hidden?: boolean }[] = [
    {
      id: 'learn',
      label: '개념 학습',
      icon: <BookOpen size={15} />,
      available: hasLearnContent,
      badge: learnPct > 0 ? `${learnPct}%` : undefined,
    },
    { id: 'realworld', label: '직업 스토리', icon: <MapPin size={15} />,    available: hasRealWorld },
    {
      id: 'graph',
      label: unit.id === 'N1-1' ? '소인수 탐색기' : '그래프 탐색',
      icon: (unit.id === 'N1-1') ? <Hash size={15} /> : <BarChart2 size={15} />,
      available: hasGraph || hasExplorer,
    },
    {
      id: 'problems',
      label: '문제풀이',
      icon: <PenTool size={15} />,
      available: hasProblems,
      badge: problemsSolved > 0 ? `${problemsSolved}/15` : undefined,
      hidden: unit.id === 'N1-1' || unit.id === 'N1-2' || unit.id === 'N2-1' || unit.id === 'N2-2' || unit.id === 'A1-1' || unit.id === 'A1-2',  // N1-x/N2-x/A1-x에서는 탭 완전 숨김 (A2-x는 표시)
    },
    { id: 'tutor',     label: 'AI 튜터',     icon: <Brain size={15} />,     available: hasTutor },
    {
      id: 'test',
      label: '실전 TEST',
      icon: <Trophy size={15} />,
      available: hasTest,
      badge: testCount > 0 ? (testPassed ? '합격' : `${testBestScore}점`) : 'NEW',
      hidden: false,
    },
  ]

  // 오답→개념 탭 이동 핸들러
  const handleGoLearn = (stepId: string) => {
    setLearnTargetStepId(stepId)
    setActiveTab('learn')
  }

  return (
    <div className="flex flex-col h-full">
      {/* ── 상단 Breadcrumb + 유닛 헤더 ─────────────────────────────────────── */}
      <div className={`${color.bg} border-b-2 ${color.border} px-6 pt-4 pb-0 flex-shrink-0`}>
        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-xs text-slate-500 mb-3 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors">대시보드</Link>
          <ChevronRight size={12} className="text-slate-300" />
          <Link to="/syllabus" className="hover:text-primary-600 transition-colors flex items-center gap-1">
            <Layers size={11} /> 전체 커리큘럼
          </Link>
          <ChevronRight size={12} className="text-slate-300" />
          <span className={`font-semibold ${color.accent}`}>
            {GRADE_SHORT[chapter.grade]}
          </span>
          <ChevronRight size={12} className="text-slate-300" />
          <span className={`font-semibold ${color.accent}`}>
            {DOMAIN_EMOJIS[chapter.domain]} {DOMAIN_LABELS[chapter.domain]}
          </span>
          <ChevronRight size={12} className="text-slate-300" />
          <span className="font-bold text-slate-700">{unit.title}</span>
        </nav>

        {/* 유닛 헤더 */}
        <div className="flex items-start gap-4 mb-4">
          <button
            onClick={() => navigate('/syllabus')}
            className="mt-0.5 p-1.5 rounded-lg hover:bg-white/60 transition-colors text-slate-500"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="text-3xl flex-shrink-0">{unit.emoji}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-xl font-black text-slate-800">{unit.title}</h1>
              {unit.isCurrent && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white font-bold animate-pulse">
                  현재 학습 중
                </span>
              )}
              {unit.contentReady && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                  ✅ 콘텐츠 완성
                </span>
              )}
              {testPassed && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-bold flex items-center gap-1">
                  🏆 단원 합격
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 mb-2">{unit.description}</p>
            {unit.story && (
              <p className="text-xs text-slate-500 italic leading-relaxed">{unit.story}</p>
            )}

            {/* ── 실시간 진도 현황 (progressStore 기반) ─────────────────── */}
            {unit.id === 'F2-1' && (
              <div className="mt-2 flex gap-3 flex-wrap text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <BookOpen size={10} className="text-blue-400" />
                  <span className="font-semibold text-blue-600">{learnCompletedCount}/{TOTAL_LEARN_STEPS}</span> 개념
                </span>
                <span className="flex items-center gap-1">
                  <PenTool size={10} className="text-purple-400" />
                  <span className="font-semibold text-purple-600">{problemsSolved}/15</span> 문제
                </span>
                <span className="flex items-center gap-1">
                  <Trophy size={10} className={testPassed ? 'text-emerald-500' : 'text-slate-400'} />
                  {testCount > 0
                    ? <span className={`font-bold ${testPassed ? 'text-emerald-600' : 'text-orange-600'}`}>{testBestScore}점</span>
                    : <span className="text-slate-400">미응시</span>
                  }
                </span>
              </div>
            )}
          </div>

          {/* 토픽 리스트 미니 */}
          <div className="hidden lg:block w-48 flex-shrink-0">
            <p className="text-xs font-bold text-slate-500 mb-1.5">학습 목차</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {unit.topics.map((topic: SubTopic, i: number) => {
                const sc = STATUS_CONFIG[topic.status]
                const TIcon = sc.icon
                return (
                  <div key={topic.id} className="flex items-center gap-1.5">
                    <TIcon size={11} className={`flex-shrink-0 ${sc.color}`} />
                    <span className={`text-xs truncate ${
                      topic.status === 'locked' ? 'text-slate-300' :
                      topic.status === 'completed' ? 'text-slate-400 line-through' :
                      'text-slate-700 font-medium'
                    }`}>{i + 1}. {topic.title}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        {/* 탭 버튼 */}
        <div className="flex gap-0.5 -mb-px flex-wrap">
          {tabs.filter(tab => !tab.hidden).map(tab => (
            <button
              key={tab.id}
              onClick={() => {
                if (tab.available) {
                  // 다른 탭으로 이동 시 learnTargetStepId 초기화
                  if (tab.id !== 'learn') setLearnTargetStepId(null)
                  setActiveTab(tab.id)
                }
              }}
              className={`relative flex items-center gap-1.5 px-3 py-2.5 text-sm font-semibold rounded-t-xl border-2 transition-all ${
                tab.id === 'test' && tab.available
                  ? activeTab === tab.id
                    ? 'bg-white border-yellow-400 border-b-white text-yellow-700'
                    : 'bg-yellow-400/30 border-yellow-300 text-yellow-800 hover:bg-yellow-400/50'
                  : tab.id === 'problems' && tab.available
                    ? activeTab === tab.id
                      ? `bg-white ${color.border} border-b-white text-slate-800`
                      : 'bg-purple-50/60 border-purple-200 text-purple-700 hover:bg-purple-50'
                    : activeTab === tab.id
                      ? `bg-white ${color.border} border-b-white text-slate-800`
                      : tab.available
                        ? `bg-white/40 border-transparent text-slate-500 hover:bg-white/70 hover:text-slate-700`
                        : 'bg-white/20 border-transparent text-slate-300 cursor-not-allowed'
              }`}
              title={!tab.available ? '아직 준비 중입니다' : ''}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              {/* 진도/점수 배지 */}
              {tab.badge && tab.available && (
                <span className={`ml-0.5 text-xs px-1.5 py-0.5 rounded-full font-black leading-none ${
                  tab.id === 'test'
                    ? tab.badge === '합격'
                      ? 'bg-emerald-500 text-white'
                      : tab.badge === 'NEW'
                        ? 'bg-yellow-400 text-yellow-900'
                        : 'bg-orange-400 text-white'
                    : tab.id === 'learn'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-purple-100 text-purple-700'
                }`}>
                  {tab.badge}
                </span>
              )}
              {!tab.available && <Lock size={11} className="ml-0.5" />}
            </button>
          ))}
        </div>
      </div>

      {/* ── 탭 콘텐츠 ────────────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        {/* 진도 패널 — F2-1 전용 (N1-1은 자체 헤더 표시) */}
        {unit.id === 'F2-1' && ['learn', 'problems', 'test'].includes(activeTab) && (
          <div className="px-6 pt-4">
            <ProgressPanel
              unitId={unit.id}
              learnCompletedCount={learnCompletedCount}
              learnPct={learnPct}
              problemsSolved={problemsSolved}
              testBestScore={testBestScore}
              testCount={testCount}
              testPassed={testPassed}
            />
          </div>
        )}

        {/* 콘텐츠 없는 유닛 안내 */}
        {!hasLearnContent && activeTab === 'learn' && (
          <div className="flex flex-col items-center justify-center h-full text-center p-12">
            <div className="text-6xl mb-4">🚧</div>
            <h2 className="text-xl font-bold text-slate-700 mb-2">콘텐츠 준비 중</h2>
            <p className="text-slate-500 max-w-md">
              이 유닛의 학습 콘텐츠는 현재 개발 중입니다.<br />
              지금은 <strong>일차함수(F2-1)</strong> 유닛만 완성되어 있으며,
              곧 나머지 유닛들도 추가될 예정입니다!
            </p>
            <Link
              to="/syllabus"
              className="mt-6 px-4 py-2 rounded-xl bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 transition-colors"
            >
              ← 커리큘럼으로 돌아가기
            </Link>
          </div>
        )}

        {activeTab === 'learn' && hasLearnContent && (
          unit.id === 'N1-1'
            ? <N1LearnPage defaultStepId={learnTargetStepId} />
            : unit.id === 'N1-2'
              ? <N2LearnPage defaultStepId={learnTargetStepId} />
              : unit.id === 'N2-1'
                ? <N3LearnPage defaultStepId={learnTargetStepId} />
                : unit.id === 'N2-2'
                  ? <N4LearnPage defaultStepId={learnTargetStepId} />
                  : unit.id === 'A1-1'
                    ? <A1LearnPage defaultStepId={learnTargetStepId} />
                    : unit.id === 'G1-1'
                      ? <G1_1LearnPage defaultStepId={learnTargetStepId} />
                      : unit.id === 'G1-2'
                        ? <G1_2LearnPage defaultStepId={learnTargetStepId} />
                        : unit.id === 'G1-3'
                          ? <G1_3LearnPage defaultStepId={learnTargetStepId} />
                          : unit.id === 'F1-1'
                            ? <F1_1LearnPage defaultStepId={learnTargetStepId} />
                            : unit.id === 'S1-1'
                              ? <S1_1LearnPage defaultStepId={learnTargetStepId} />
                              : unit.id === 'A2-1'
                                ? <A2_1LearnPage defaultStepId={learnTargetStepId} />
                                : unit.id === 'A2-2'
                                  ? <A2_2LearnPage defaultStepId={learnTargetStepId} />
                                  : unit.id === 'A2-3'
                                    ? <A2_3LearnPage defaultStepId={learnTargetStepId} />
                                    : unit.id === 'F2-2'
                                      ? <F2_2LearnPage defaultStepId={learnTargetStepId} />
                                      : <LearnPage defaultStepId={learnTargetStepId} />
        )}
        {activeTab === 'realworld' && hasRealWorld && (
          unit.id === 'N1-1'
            ? <N1RealWorldPage />
            : unit.id === 'N1-2'
              ? <N1_2RealWorldPage />
              : unit.id === 'N2-2'
                ? <N4RealWorldPage />
                : unit.id === 'A1-1'
                  ? <A1RealWorldPage />
                  : unit.id === 'A1-2'
                    ? <A1_2RealWorldPage />
                    : unit.id === 'G1-1'
                      ? <G1_1RealWorldPage />
                      : unit.id === 'G1-2'
                        ? <G1_2RealWorldPage />
                        : unit.id === 'G1-3'
                          ? <G1_3RealWorldPage />
                          : unit.id === 'F1-1'
                            ? <F1_1RealWorldPage />
                            : unit.id === 'S1-1'
                              ? <S1_1RealWorldPage />
                              : unit.id === 'A2-1'
                                ? <A2_1RealWorldPage />
                                : unit.id === 'A2-2'
                                  ? <A2_2RealWorldPage />
                                  : unit.id === 'A2-3'
                                    ? <A2_3RealWorldPage />
                                    : unit.id === 'F2-2'
                                      ? <F2_2RealWorldPage />
                                      : <RealWorldPage />
        )}
        {activeTab === 'graph' && hasGraph && <GraphPage />}
        {activeTab === 'graph' && hasExplorer && <N1ExplorerPanel />}
        {activeTab === 'problems' && hasProblems && (
          unit.id === 'A2-1'
            ? <A2_1ProblemPage />
            : unit.id === 'A2-2'
              ? <A2_2ProblemPage />
              : unit.id === 'A2-3'
                ? <A2_3ProblemPage />
                : unit.id === 'F2-2'
                  ? <F2_2ProblemPage />
                  : <ProblemPage />
        )}
        {activeTab === 'tutor' && (
          unit.id === 'N1-1'
            ? <N1TutorPage />
            : unit.id === 'N1-2'
              ? <N2TutorPage />
              : unit.id === 'N2-1'
                ? <N3TutorPage />
                : unit.id === 'N2-2'
                  ? <N4TutorPage />
                  : unit.id === 'A1-1'
                    ? <A1TutorPage />
                    : unit.id === 'A1-2'
                      ? <A1_2TutorPage />
                      : unit.id === 'G1-1'
                        ? <G1_1TutorPage />
                        : unit.id === 'G1-2'
                          ? <G1_2TutorPage />
                          : unit.id === 'G1-3'
                            ? <G1_3TutorPage />
                            : unit.id === 'F1-1'
                              ? <F1_1TutorPage />
                              : unit.id === 'S1-1'
                                ? <S1_1TutorPage />
                                : <TutorPage />
        )}
        {activeTab === 'test' && hasTest && (
          unit.id === 'N1-1'
            ? <N1TestPage onGoLearn={handleGoLearn} />
            : unit.id === 'N1-2'
              ? <N2TestPage onGoLearn={handleGoLearn} />
              : unit.id === 'N2-1'
                ? <N3TestPage onGoLearn={handleGoLearn} />
                : unit.id === 'N2-2'
                  ? <N4TestPage onGoLearn={handleGoLearn} />
                  : unit.id === 'A1-1'
                    ? <A1TestPage onGoLearn={handleGoLearn} />
                    : unit.id === 'A1-2'
                      ? <A1_2TestPage onGoLearn={handleGoLearn} />
                      : unit.id === 'G1-1'
                        ? <G1_1TestPage onGoLearn={handleGoLearn} />
                        : unit.id === 'G1-2'
                          ? <G1_2TestPage onGoLearn={handleGoLearn} />
                          : unit.id === 'G1-3'
                            ? <G1_3TestPage onGoLearn={handleGoLearn} />
                            : unit.id === 'F1-1'
                              ? <F1_1TestPage onGoLearn={handleGoLearn} />
                              : unit.id === 'S1-1'
                                ? <S1_1TestPage onGoLearn={handleGoLearn} />
                                : unit.id === 'A2-1'
                                  ? <A2_1TestPage onGoLearn={handleGoLearn} />
                                  : unit.id === 'A2-2'
                                    ? <A2_2TestPage onGoLearn={handleGoLearn} />
                                    : unit.id === 'A2-3'
                                      ? <A2_3TestPage onGoLearn={handleGoLearn} />
                                      : unit.id === 'F2-2'
                                        ? <F2_2TestPage onGoLearn={handleGoLearn} />
                                        : <TestPage onGoLearn={handleGoLearn} />
        )}
      </div>
    </div>
  )
}
